// src/components/FrameworkAccordion.js
import React, { useState, useMemo } from 'react';
import ComponentDetailsList from './ComponentDetailsList';
import { ALL_FRAMEWORK_COMPONENTS, FRAMEWORK_PARTS } from './frameworkDefinition';

const FrameworkAccordion = ({ healthData, frameworkComponents, onViewDetails, loadingComponentId }) => {
  const [expandedParts, setExpandedParts] = useState(new Set());

  // Merge API data with complete component list
  const completeComponentList = useMemo(() => {
    // Create a map of API data by component ID
    const apiDataMap = new Map();
    if (frameworkComponents && Array.isArray(frameworkComponents)) {
      frameworkComponents.forEach(comp => {
        apiDataMap.set(comp.componentId, comp);
      });
    }

    // Merge: use API data if exists, otherwise mark as absent
    return ALL_FRAMEWORK_COMPONENTS.map(templateComp => {
      const apiData = apiDataMap.get(templateComp.id);
      
      if (apiData) {
        // API data exists - use it and preserve all S3 fields
        return {
          ...apiData,  // Spread all fields from S3 data (isDetected, context, expectedValue, etc.)
          componentId: apiData.componentId,
          componentName: apiData.componentName,
          partNumber: apiData.partNumber || templateComp.partNumber,
          weight: apiData.weight || templateComp.weight,
          present: apiData.present,
          deferred: templateComp.deferred,
          hasData: true  // Mark that this component has actual S3 data
        };
      } else {
        // No API data - component is absent
        return {
          componentId: templateComp.id,
          componentName: templateComp.name,
          partNumber: templateComp.partNumber,
          weight: templateComp.weight,
          present: false,
          isDetected: false,
          context: null,
          expectedValue: true,
          textSnippet: null,
          justification: null,
          evaluationDate: null,
          communicationId: null,
          deferred: templateComp.deferred,
          hasData: false  // Mark that this component has no S3 data
        };
      }
    });
  }, [frameworkComponents]);

  const togglePart = (partNumber) => {
    const newExpanded = new Set(expandedParts);
    if (newExpanded.has(partNumber)) {
      newExpanded.delete(partNumber);
    } else {
      newExpanded.add(partNumber);
    }
    setExpandedParts(newExpanded);
  };

  const getPartComponents = (partNumber) => {
    return completeComponentList.filter(c => c.partNumber === partNumber);
  };

  const calculatePartMetrics = (partNumber, partInfo) => {
    const components = getPartComponents(partNumber);
    
    // Helper to check if component is negative metric
    const isNegativeMetric = (compId) => {
      return compId === 'churn_risk_indicator' || compId === 'competitive_threat';
    };
    
    // Count components that MEET EXPECTATIONS (excluding deferred)
    // IMPORTANT: Only count components that have actual S3 data (hasData: true)
    // When there's no S3 data, we don't know if expectations are met, so we don't count them
    // For positive metrics: detected = meets expectation
    // For negative metrics: NOT detected = meets expectation
    const meetsExpectationCount = components.filter(c => {
      if (c.deferred) return false;
      // Only count if we have actual S3 data for this component
      if (!c.hasData) return false;
      
      // Use pre-calculated meetsExpectation if available (handles new polarity logic)
      if (typeof c.meetsExpectation !== 'undefined') {
          return c.meetsExpectation;
      }

      // Fallback legacy logic
      const isNegative = isNegativeMetric(c.componentId);
      return isNegative ? !c.present : c.present;
    }).length;

    // FIXED: Use Neo4j ClientEval node as source of truth for part scores
    // The ClientEval node (healthData) contains the authoritative part1-part6 scores
    const partScore = healthData ? healthData[`part${partNumber}`] || 0 : 0;

    return {
      meetsExpectationCount,
      activeComponents: partInfo.activeComponents,
      partScore: Math.round(partScore)
    };
  };

  if (!completeComponentList || completeComponentList.length === 0) {
    return (
      <div 
        className="rounded-xl p-6"
        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
      >
        <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--pallium-text-primary)' }}>Framework Score Breakdown</h2>
        <p className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>Loading framework components...</p>
      </div>
    );
  }

  return (
    <div 
      className="rounded-xl"
      style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
    >
      <div 
        className="px-6 py-4"
        style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>Framework Score Breakdown</h2>
        <p className="text-sm mt-1" style={{ color: 'var(--pallium-text-secondary)' }}>
          43 active components across 6 framework parts
        </p>
      </div>

      <div>
        {FRAMEWORK_PARTS.map((part) => {
          const isExpanded = expandedParts.has(part.number);
          const metrics = calculatePartMetrics(part.number, part);
          const completionPercentage = (metrics.meetsExpectationCount / metrics.activeComponents) * 100;
          const components = getPartComponents(part.number);

          return (
            <div 
              key={part.number} 
              className="transition-all duration-200"
              style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}
            >
              {/* Part Header - Clickable */}
              <button
                onClick={() => togglePart(part.number)}
                className="w-full px-6 py-4 flex items-center justify-between transition-colors"
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="flex-1 text-left">
                  {/* Part Name and Description */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-md font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>
                        Part {part.number}: {part.name}
                      </h3>
                      <p className="text-xs mt-1" style={{ color: 'var(--pallium-text-secondary)' }}>
                        {part.description}
                      </p>
                    </div>
                    
                    {/* Expand/Collapse Icon */}
                    <div className="ml-4 flex-shrink-0">
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${
                          isExpanded ? 'transform rotate-180' : ''
                        }`}
                        style={{ color: 'var(--pallium-primary-purple)' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Metrics Row */}
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>
                        {metrics.meetsExpectationCount}/{metrics.activeComponents} components
                      </span>
                      <span className="ml-1" style={{ color: 'var(--pallium-text-secondary)' }}>
                        meet expectations ({metrics.partScore}%)
                      </span>
                    </div>
                    
                    <div style={{ color: 'var(--pallium-text-secondary)' }}>
                      {part.weight}% of total score
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div 
                      className="w-full rounded-full h-2"
                      style={{ backgroundColor: 'var(--pallium-purple-1)' }}
                    >
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${completionPercentage}%`,
                          backgroundColor: 'var(--pallium-primary-purple)'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </button>

              {/* Component Details - Expandable */}
              {isExpanded && (
                <div 
                  style={{ 
                    backgroundColor: 'var(--pallium-purple-0-5)', 
                    borderTop: '1px solid var(--pallium-stroke-light)' 
                  }}
                >
                  <ComponentDetailsList
                    components={components}
                    partNumber={part.number}
                    onViewDetails={onViewDetails}
                    loadingComponentId={loadingComponentId}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FrameworkAccordion;
